import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getBoxPackages } from '../../data/API';
import './PackageRecommendationWidget.css';

const PackageRecommendationWidget = ({ onOrderClick, forceCategory }) => {
    const [step, setStep] = useState(1);
    const [sqft, setSqft] = useState('');
    const [belongings, setBelongings] = useState('');
    const [recommendations, setRecommendations] = useState([]);
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const reduxCategory = useSelector(state => state.boxPackage.category);
    const category = forceCategory || reduxCategory;
    const navigate = useNavigate();

    // Default order button action
    const handleOrderNow = () => {
        if (onOrderClick) {
            onOrderClick();
        } else {
            navigate('/pricing');
        }
    };

    useEffect(() => {
        setLoading(true);
        getBoxPackages(category, 'Box Packages', '1')
            .then((res) => {
                const packagesWithSqft = res.data.results.filter(
                    pkg => pkg.sqft_min != null && pkg.sqft_max != null
                ).sort((a, b) => a.total_boxes - b.total_boxes);
                setPackages(packagesWithSqft);
                setLoading(false);
            })
            .catch((e) => {
                console.error('Failed to fetch packages for widget:', e);
                setLoading(false);
            });
    }, [category]);

    const getRecommendations = (squareFootage, belongingsAmount) => {
        const sqftNum = parseInt(squareFootage);
        const recommendations = [];
        const buffer = 150;

        const relevantPackages = packages.map((pkg, index) => {
            const inRange = sqftNum >= pkg.sqft_min && sqftNum <= pkg.sqft_max;

            const nearMin = sqftNum >= (pkg.sqft_min - buffer) && sqftNum < pkg.sqft_min;
            const nearMax = sqftNum > pkg.sqft_max && sqftNum <= (pkg.sqft_max + buffer);

            let fitScore = 0;
            if (inRange) {
                fitScore = 100;
            } else if (nearMin || nearMax) {
                const distance = nearMin ? (pkg.sqft_min - sqftNum) : (sqftNum - pkg.sqft_max);
                fitScore = Math.max(0, 100 - distance);
            }

            return { pkg, index, fitScore };
        })
            .filter(item => item.fitScore > 0)
            .sort((a, b) => b.fitScore - a.fitScore);

        if (relevantPackages.length === 0) {
            const closest = packages.reduce((prev, curr) => {
                const prevDist = Math.min(
                    Math.abs(sqftNum - prev.sqft_min),
                    Math.abs(sqftNum - prev.sqft_max)
                );
                const currDist = Math.min(
                    Math.abs(sqftNum - curr.sqft_min),
                    Math.abs(sqftNum - curr.sqft_max)
                );
                return currDist < prevDist ? curr : prev;
            });
            relevantPackages.push({ pkg: closest, index: packages.indexOf(closest), fitScore: 50 });
        }

        if (belongingsAmount === 'low') {
            const pkgsToRecommend = relevantPackages.slice(0, 2);

            pkgsToRecommend.forEach((item, idx) => {
                const pkg = item.pkg;
                recommendations.push({
                    package_id: pkg.id,
                    package_name: pkg.title,
                    bins: pkg.total_boxes,
                    sqft_min: pkg.sqft_min,
                    sqft_max: pkg.sqft_max,
                    rationale: idx === 0
                        ? `Perfect for your ${sqftNum} sqft space with minimal belongings. The ${pkg.title} package provides ${pkg.total_boxes} boxes.`
                        : `Another great option for your ${sqftNum} sqft space with light belongings. The ${pkg.title} package includes ${pkg.total_boxes} boxes.`
                });
            });
        } else if (belongingsAmount === 'medium') {
            const pkgsToRecommend = relevantPackages.slice(0, 2);

            pkgsToRecommend.forEach((item, idx) => {
                const pkg = item.pkg;
                recommendations.push({
                    package_id: pkg.id,
                    package_name: pkg.title,
                    bins: pkg.total_boxes,
                    sqft_min: pkg.sqft_min,
                    sqft_max: pkg.sqft_max,
                    rationale: idx === 0
                        ? `Best fit for your ${sqftNum} sqft space with a moderate amount of belongings. The ${pkg.title} package offers ${pkg.total_boxes} boxes.`
                        : `Also recommended for your ${sqftNum} sqft space. The ${pkg.title} package provides ${pkg.total_boxes} boxes for moderate packers.`
                });
            });
        } else if (belongingsAmount === 'high') {
            const pkgsToRecommend = relevantPackages.slice(0, 3);

            pkgsToRecommend.forEach((item, idx) => {
                const pkg = item.pkg;
                recommendations.push({
                    package_id: pkg.id,
                    package_name: pkg.title,
                    bins: pkg.total_boxes,
                    sqft_min: pkg.sqft_min,
                    sqft_max: pkg.sqft_max,
                    rationale: idx === 0
                        ? `Recommended for your ${sqftNum} sqft space with lots of belongings. The ${pkg.title} package with ${pkg.total_boxes} boxes should accommodate your items.`
                        : idx === 1
                            ? `Great alternative for your ${sqftNum} sqft space. The ${pkg.title} package offers ${pkg.total_boxes} boxes for heavy packers.`
                            : `Consider the ${pkg.title} package with ${pkg.total_boxes} boxes for maximum capacity in your ${sqftNum} sqft space.`
                });
            });
        }

        return recommendations;
    };

    const handleSqftSubmit = (value) => {
        setSqft(value);
        setStep(2);
    };

    const handleBelongingsSubmit = (value) => {
        setBelongings(value);
        const recs = getRecommendations(sqft, value);
        setRecommendations(recs);
        setStep(3);
    };

    const resetWidget = () => {
        setStep(1);
        setSqft('');
        setBelongings('');
        setRecommendations([]);
    };

    const goBackToStep = (stepNumber) => {
        setStep(stepNumber);
        if (stepNumber === 1) {
            setBelongings('');
            setRecommendations([]);
        }
    };

    const goBackOneStep = () => {
        if (step === 3) {
            setStep(2);
            setRecommendations([]);
        } else if (step === 2) {
            setStep(1);
            setBelongings('');
        }
    };

    // Show loading state
    if (loading) {
        return (
            <div className="package-widget-container">
                <div className="package-widget">
                    <h3 className="widget-title">Find the best package type to fit your needs</h3>
                    <p className="widget-loading">Loading packages...</p>
                </div>
            </div>
        );
    }

    if (packages.length === 0) {
        return null;
    }

    return (
        <div className="package-widget-container">
            <div className="package-widget">
                {/* Back button at the top - only show after step 1 */}
                {step > 1 && (
                    <button onClick={goBackOneStep} className="widget-top-back-button">
                        ← Back
                    </button>
                )}

                <h3 className="widget-title">Find the best package type to fit your needs</h3>

                {step === 1 && (
                    <div className="widget-step">
                        <p className="widget-question">What is the approximate square footage of your home?</p>
                        <div className="widget-options">
                            <button onClick={() => handleSqftSubmit('400')} className="widget-button">
                                Under 500 sqft
                            </button>
                            <button onClick={() => handleSqftSubmit('650')} className="widget-button">
                                500 - 800 sqft
                            </button>
                            <button onClick={() => handleSqftSubmit('1000')} className="widget-button">
                                800 - 1,200 sqft
                            </button>
                            <button onClick={() => handleSqftSubmit('1500')} className="widget-button">
                                1,200 - 1,800 sqft
                            </button>
                            <button onClick={() => handleSqftSubmit('2150')} className="widget-button">
                                1,800 - 2,500 sqft
                            </button>
                            <button onClick={() => handleSqftSubmit('3000')} className="widget-button">
                                Over 2,500 sqft
                            </button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="widget-step">
                        <p className="widget-question">Do you have a lot of belongings/personal items?</p>
                        <div className="widget-options">
                            <button onClick={() => handleBelongingsSubmit('low')} className="widget-button">
                                Minimal - Light packer
                            </button>
                            <button onClick={() => handleBelongingsSubmit('medium')} className="widget-button">
                                Moderate - Average amount
                            </button>
                            <button onClick={() => handleBelongingsSubmit('high')} className="widget-button">
                                A lot - Heavy packer
                            </button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="widget-step widget-results">
                        <p className="widget-results-title">Recommended Packages:</p>
                        <div className="recommendations custom-reccomendation-wrapper">
                            {recommendations.map((rec, index) => (
                                <div key={rec.package_id || index} className="recommendation-card">
                                    <h4 className="rec-package-name">{rec.package_name}</h4>
                                    <div className="rec-details">
                                        <p className="rec-bins">{rec.bins} Boxes</p>
                                        <p className="rec-sqft">Recommended for {rec.sqft_min} - {rec.sqft_max} sqft</p>
                                    </div>
                                    <p className="rec-rationale">{rec.rationale}</p>
                                </div>
                            ))}
                        </div>
                        <div className="widget-actions">
                            <button onClick={handleOrderNow} className="widget-order-button">
                                Order Now
                            </button>
                            <p className="widget-scroll-hint">Scroll down to view all packages and order</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

PackageRecommendationWidget.propTypes = {
    onOrderClick: PropTypes.func,
    forceCategory: PropTypes.string
};

export default PackageRecommendationWidget;